import { useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system';

import { useLazyGetDocumentsQuery } from 'services/documents/documents';
import { GetDocumentsResProps } from 'services/documents/documents.types';

import InsuranceExamples from 'modals/InsuranceExamples';
import VerifyInsurance from 'modals/VerifyInsurance';
import { setInitialDocsStatus } from 'widgets/Insurance/insurance.settings';
import { InsuranceDocsProps } from 'widgets/Insurance/insurance.types';

import { handleRequestCatch } from 'utils/helpers';

const UploadInsurance = ({
  accessToken,
  onUpdateFile
}: {
  accessToken?: string;
  onUpdateFile: (value: string[]) => void;
}) => {
  const [getDocuments] = useLazyGetDocumentsQuery();

  const [showModal, toggleShowModal] = useToggle(false);
  const [isOpenVerifyInsurance, toggleIsOpenVerifyInsurance] = useToggle(false);
  const [filesStatus, setFilesStatus] = useState<InsuranceDocsProps>({
    back: { fileName: '', filePath: '', fileStatus: 'initial' },
    front: { fileName: '', filePath: '', fileStatus: 'initial' }
  });
  const [verifyInsuranceType, setVerifyInsuranceType] = useState<'front' | 'back'>('front');

  const card = (type: 'front' | 'back') => {
    return (
      <div
        className="relative flex flex-1 cursor-pointer flex-col items-center rounded-xl bg-white py-3 shadow"
        data-testid={`file_zone_${type}`}
        onClick={() => {
          setVerifyInsuranceType(type);
          toggleIsOpenVerifyInsurance();
        }}
      >
        {filesStatus[type].fileStatus === 'success' && (
          <Common.Icon className="absolute right-2 top-2 text-green" name="check-circle" />
        )}
        <Common.Icon className="size-12 text-primary-400" name="identification" />
        <p className="mb-4 font-semibold first-letter:uppercase">{type}</p>
        <Common.Button color="white">
          {filesStatus[type].fileStatus === 'success' ? 'View' : 'Upload'}
        </Common.Button>
      </div>
    );
  };

  const handleGetDocuments = ({ data }: GetDocumentsResProps) => {
    if (data) {
      const initialStatus = setInitialDocsStatus(
        filesStatus,
        data,
        data[0]?.insuranceCard?.insuranceId
      );
      setFilesStatus(initialStatus);
    }
  };

  const onInit = () => {
    getDocuments({ accessToken, category: 'insurance', pageNo: 0 })
      .unwrap()
      .then(handleGetDocuments)
      .catch(handleRequestCatch);
  };

  useEffect(onInit, []);

  useEffect(() => {
    const IDs = [filesStatus.front._id, filesStatus.back._id].filter(Boolean);
    onUpdateFile(IDs as string[]);
  }, [filesStatus]);

  return (
    <div className="mx-auto flex w-full flex-col gap-4">
      <InsuranceExamples isOpen={showModal} onClose={toggleShowModal} />
      <VerifyInsurance
        accessToken={accessToken}
        filesStatus={filesStatus[verifyInsuranceType]}
        isOpen={isOpenVerifyInsurance}
        setFilesStatus={(file) => setFilesStatus({ ...filesStatus, [verifyInsuranceType]: file })}
        type={verifyInsuranceType}
        onClose={toggleIsOpenVerifyInsurance}
      />
      <h3 className="font-bold text-primary-700">Insurance card photos</h3>
      <div className="flex w-full gap-3">
        {card('front')}
        {card('back')}
      </div>
      <Common.Button
        className="mx-auto"
        color="white"
        size="md"
        type="button"
        onClick={toggleShowModal}
      >
        See example
      </Common.Button>
    </div>
  );
};

export default UploadInsurance;
